import { useEffect, useRef, type MutableRefObject } from 'react';
import { brandBright, brandDark } from '@/components/notes/brandColor';

interface Props {
  /** Live mic level 0..1 (updated by the recorder). */
  levelRef: MutableRefObject<number>;
  active: boolean;
  height?: number;
}

/**
 * Recording visualizer matching the concept art: a glowing center ring (mic overlaid) with
 * flowing, layered waveform LINES radiating out — violet on the left, cyan/blue on the right.
 * The waves always travel (never static); the mic level scales their amplitude. Rendered on a
 * canvas via requestAnimationFrame so it animates smoothly on web and mobile.
 */
export function NoteWaveRing({ levelRef, active, height = 150 }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef(0);
  const phaseRef = useRef(0);
  const ampRef = useRef(0.2); // smoothed amplitude so level changes ease in/out

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      const w = canvas.clientWidth;
      canvas.width = w * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);
    // Re-measure whenever the canvas's own box changes (layout settle, panel resize,
    // font load) — otherwise the drawn ring's center (cx = clientWidth/2) goes stale
    // and drifts away from the CSS-centered mic.
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    // Per-side layered line styles (near→far): color + opacity + width.
    const LAYERS = [
      { o: 0.95, wdt: 2.2, f1: 9, f2: 5, sp: 1.0 },
      { o: 0.6, wdt: 1.5, f1: 12, f2: 7, sp: 1.35 },
      { o: 0.4, wdt: 1.3, f1: 7, f2: 4, sp: 0.8 },
      { o: 0.22, wdt: 1.1, f1: 15, f2: 9, sp: 1.7 },
    ];
    const LEFT = brandBright(); // brand (bright)
    const RIGHT = brandDark(); // brand (deep)

    const draw = () => {
      const w = canvas.clientWidth;
      const h = height;
      const cx = w / 2;
      const cy = h / 2;
      ctx.clearRect(0, 0, w, h);

      // Always-moving phase; amplitude eases toward a level-driven target (with a live baseline
      // so the wave keeps rippling even in silence).
      phaseRef.current += 0.09;
      const target = active ? 0.28 + 0.72 * levelRef.current : 0.16;
      ampRef.current += (target - ampRef.current) * 0.15;
      const amp = ampRef.current;

      const ringR = Math.min(h * 0.32, 44);
      const maxAmp = h * 0.42;
      const steps = 64;

      const drawSide = (dir: -1 | 1, rgb: string) => {
        const startX = cx + dir * (ringR + 6);
        const endX = dir < 0 ? 4 : w - 4;
        const span = Math.abs(endX - startX);
        if (span < 10) return;

        LAYERS.forEach((L, li) => {
          ctx.beginPath();
          for (let s = 0; s <= steps; s++) {
            const tt = s / steps; // 0 at ring → 1 at edge
            const x = startX + dir * tt * span;
            const env = Math.pow(1 - tt, 0.75); // tall near ring, taper out
            const wave =
              Math.sin(phaseRef.current * L.sp + tt * L.f1 + li * 1.3) * 0.6 +
              Math.sin(phaseRef.current * L.sp * 1.6 + tt * L.f2 - li * 0.7) * 0.4;
            const y = cy + wave * env * maxAmp * amp * (li % 2 === 0 ? 1 : -1);
            if (s === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          }
          ctx.strokeStyle = `rgba(${rgb},${L.o})`;
          ctx.lineWidth = L.wdt;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.shadowColor = `rgba(${rgb},0.5)`;
          ctx.shadowBlur = 6;
          ctx.stroke();
        });
      };

      drawSide(-1, LEFT);
      drawSide(1, RIGHT);

      // Center ring.
      ctx.shadowBlur = 18;
      ctx.shadowColor = `rgba(${LEFT},0.7)`;
      const rg = ctx.createLinearGradient(cx - ringR, cy, cx + ringR, cy);
      rg.addColorStop(0, `rgb(${LEFT})`);
      rg.addColorStop(1, `rgb(${RIGHT})`);
      ctx.strokeStyle = rg;
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.arc(cx, cy, ringR, 0, Math.PI * 2);
      ctx.stroke();
      ctx.shadowBlur = 0;

      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
      ro.disconnect();
    };
  }, [active, height, levelRef]);

  return <canvas ref={canvasRef} className="w-full" style={{ height }} />;
}

export default NoteWaveRing;
